<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div>
    <loading-provider :loading="loading">
      <template v-if="!reserveReady">
        <no-product-page />
      </template>
      <div v-else-if="reservationActivated">
        <div class="mt-4">
          <v-tabs
            v-model="tab"
            background-color="transparent"
            color="black"
            style="background: transparent"
          >
            <v-tab>{{ $t('overview') }}</v-tab>
            <v-tab>{{ $t('DeskManagement') }}</v-tab>
            <v-tab>{{ $t('RuleManagement') }}</v-tab>
            <v-tab>{{ $t('openTime') }}</v-tab>
            <v-tab>{{ $t('SpecialOpeningTimes') }}</v-tab>
          </v-tabs>
          <v-divider />
        </div>
        <v-tabs-items
          v-model="tab"
          class="mt-4"
          style="background: transparent"
        >
          <v-tab-item>
            <open-reserve />
          </v-tab-item>
          <v-tab-item :disabled="!reservationActivated">
            <useful-reserve-table />
          </v-tab-item>
          <v-tab-item :disabled="!reservationActivated">
            <table-rules />
          </v-tab-item>
          <v-tab-item :disabled="!reservationActivated">
            <reservation-open-time />
          </v-tab-item>
          <v-tab-item :disabled="!reservationActivated">
            <special-closing-time />
          </v-tab-item>
        </v-tabs-items>
      </div>
      <div v-else>
        <not-active-page
          :product-name="$t('ReservationSystem')"
          @click="activeDialog = true"
        />
      </div>
    </loading-provider>
    <v-dialog
      v-model="activeDialog"
      max-width="800px"
      persistent
    >
      <base-form
        :editing-object="reserveStatus"
        :schema="schemas"
        @close="activeDialog=false"
        @submit="saveUserInfo"
      >
        <div>{{ $t('PlannedSettings') }}</div>
        <template #subtitle>
          {{ $t('PlannedSettingsHint') }}
        </template>
      </base-form>
    </v-dialog>
  </div>
</template>

<script>
import OpenReserve from '@/views/AadenPages/settings/reserveSetting/reserveDashboard/OpenReserve.vue'
import UsefulReserveTable from '@/views/AadenPages/settings/reserveSetting/UsefulReserveTable'
import TableRules from '@/views/AadenPages/settings/reserveSetting/tableRule/TableRules.vue'
import NoProductPage from '@/views/widget/commonLogic/NoProductPage'
import { getActiveProductCode } from '@/model/dataLayer/api/SaaS/user'
import { ProductCodes } from '@/model/dataLayer/api/SaaS/product'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import { addUserInfo, getReservationUserList, ifReserveOpen } from '@/model/dataLayer/service/reservation/reservation'
import ReservationOpenTime from '@/views/AadenPages/settings/reserveSetting/openTime/ReservationOpenTime.vue'
import SpecialClosingTime from '@/views/AadenPages/settings/reserveSetting/SpecialClosingTime.vue'
import NotActivePage from '@/views/SaaS/widget/NotActivePage.vue'
import BaseForm from '@/views/widget/INK/BaseForm/BaseForm.vue'
import i18n from '@/i18n'

export default {
  name: 'ReserveIndex',
  components: {
    BaseForm,
    NotActivePage,
    SpecialClosingTime,
    ReservationOpenTime,
    LoadingProvider,
    NoProductPage,
    TableRules,
    UsefulReserveTable,
    OpenReserve
  },
  data () {
    return {
      tab: null,
      reserveReady: false,
      activeDialog: false,
      reservationActivated: false,
      reserveStatus: {
        subdomainName: '',
        mapUrl: ''
      },
      openUserList: null,
      loading: true
    }
  },
  computed: {
    schemas () {
      return [
        {
          key: 'subDominName',
          name: 'SubdomainLink',
          componentProps: {
            rules: [
              v => !!v || i18n.t('hostCantEmpty'),
              v => !this.openUserList.find(
                it => it.subDominName !== this.reserveStatus.subDominName && it.subDominName === v) ||
                i18n.t('thisHostnameAlreadyUse')
            ],
            prefix: location.protocol + '//',
            suffix: '.reservation.aaden.io'
          }
        },
        {
          key: 'mapUrl',
          name: 'GoogleMapsLink'
        }
      ]
    }
  },
  methods: {
    async saveUserInfo (info) {
      await addUserInfo(info)
      this.activeDialog = false
      this.reservationActivated = await ifReserveOpen()
    }
  },
  async mounted () {
    try {
      this.loading = true
      const res = await getActiveProductCode()
      this.openUserList = await getReservationUserList()
      this.reserveReady = res.includes(ProductCodes.reservation)
      this.reservationActivated = await ifReserveOpen()
    } catch (e) {

    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.text-slate-500 {
  --tw-text-opacity: 1;
  color: rgb(100 116 139 / var(--tw-text-opacity));
}

.text-slate-600 {
  --tw-text-opacity: 1;
  color: rgb(71 85 105 / var(--tw-text-opacity));
}
</style>
